import { useMemo } from 'react';
import CONSTANTS from 'src/store/constants';
import useModels from './swr/useModels';
import useModelsStatus from './swr/useModelsStatus';

function downloadDTO(download) {
  return {
    ...download,
    metadataPayload: {
      source_id: download?.payload?.thinkalike_model_id,
    },
    payload: {
      ...download?.payload,
      future_download: {
        ...download?.payload?.future_download,
        metadataPayload: {
          source_id: download?.payload?.thinkalike_model_id,
        },
      },
    },
  };
}

/* eslint-disable no-console */
/**
 * useScoringInProgress - gets items for a segment with scoring in progress
 *
 * @returns {object}
 */
export default function useScoringInProgress(segmentId, statuses, debug = false) {
  if (debug) {
    console.log('useScoringInProgress segmentId', segmentId);
    console.log('useScoringInProgress statuses', statuses);
  }

  // Target Model Scoring

  const targetScoringInProgressForSegment = useMemo(() => {
    const inProgressIds = {};
    const inProgressStatuses = [];

    statuses.forEach(({ payload, status }, i) => {
      const thinkalikeModelId = payload.thinkalike_model_id;

      if (status === CONSTANTS.SEGMENT.STATUS.PREDICT_SCORES_INITIATED) {
        if (!inProgressIds[thinkalikeModelId]) {
          inProgressIds[thinkalikeModelId] = [];
        }
        inProgressIds[thinkalikeModelId].push(statuses[i]);
      } else if (status === CONSTANTS.SEGMENT.STATUS.GENERATE_SCORES_DOWNLOAD_SUCCESS) {
        if (inProgressIds[thinkalikeModelId] && inProgressIds[thinkalikeModelId].length > 0) {
          inProgressIds[thinkalikeModelId].pop();
        }
      }
    });

    for (const inProgress of Object.values(inProgressIds)) {
      inProgressStatuses.push(...inProgress);
    }

    return inProgressStatuses.sort((a, b) => b.id - a.id);
  }, [statuses]);

  if (debug) {
    console.log('targetScoringInProgressForSegment', targetScoringInProgressForSegment);
  }

  // Validation Model Scoring

  const modelsQuery = useMemo(() => {
    const query = {
      filters: [{ name: 'data_model_type', op: 'neq', val: CONSTANTS.TAL_MODEL.MODEL_CLASS.ACTUARIAL }],
      fields:
        'items{id,name,framework,modelData{dataPayload{actuarial_segment_id,positive_segment_id,negative_segment_id,validation_segment_id,}},statuses,recentStatus,createdAt,dataModelType,},*',
    };
    return query;
  }, []);

  const { data: modelsList = [], isLoading: modelsLoading } = useModels({
    query: modelsQuery,
    config: { revalidateOnMount: true },
  });

  const segmentModelIds = useMemo(
    () =>
      modelsList
        .filter(({ dataModelType, modelData }) => {
          const segmentIdType = dataModelType.includes('ACT_') ? 'actuarial_segment_id' : 'validation_segment_id';
          return modelData?.dataPayload[segmentIdType] === segmentId;
        })
        .map(({ id }) => id),
    [segmentId, modelsList]
  );

  if (debug) {
    console.log('segmentModelIds', segmentModelIds);
  }

  const { data: modelsStatusList = [], isLoading: modelsStatusLoading } = useModelsStatus({
    query: {
      order_by: [
        {
          field: 'id',
          direction: 'desc',
        },
      ],
    },
    modelIds: segmentModelIds,
  });

  if (debug) {
    console.log('modelsStatusList', modelsStatusList);
  }

  const modelsScoringInProgressForSegment = useMemo(
    () =>
      modelsStatusList
        .map((statusList, i) => {
          const filteredStatuses = statusList
            .sort((a, b) => b.id - a.id)
            .filter(
              ({ status }) =>
                status === CONSTANTS.TAL_MODEL.STATE.MODEL_GENERATION_INITIATED ||
                status === CONSTANTS.TAL_MODEL.STATE.MODEL_GENERATION_SUCCESS
            );
          const status = filteredStatuses?.[0];
          if (!status || status?.status === CONSTANTS.TAL_MODEL.STATE.MODEL_GENERATION_SUCCESS) return null;
          status.payload.thinkalike_model_id = modelsList[i].id;
          return status;
        })
        .filter((elem) => elem)
        .sort((a, b) => b.id - a.id),
    [modelsList, modelsStatusList]
  );

  if (debug) {
    console.log('modelsScoringInProgressForSegment', modelsScoringInProgressForSegment);
  }

  // Results

  const hasScoringInProgress = useMemo(
    () => !!targetScoringInProgressForSegment.length || !!modelsScoringInProgressForSegment.length,
    [targetScoringInProgressForSegment, modelsScoringInProgressForSegment]
  );

  const scoringInProgressItems = useMemo(
    () => [...targetScoringInProgressForSegment, ...modelsScoringInProgressForSegment].map(downloadDTO),
    [targetScoringInProgressForSegment, modelsScoringInProgressForSegment]
  );

  const loading = useMemo(() => modelsLoading || modelsStatusLoading, [modelsLoading, modelsStatusLoading]);

  if (!segmentId) return { hasScoringInProgress: [], scoringInProgressItems: [], loading };

  if (debug) {
    console.log('hasScoringInProgress', hasScoringInProgress);
    console.log('scoringInProgressItems', scoringInProgressItems);
  }

  return { hasScoringInProgress, scoringInProgressItems, loading };
}
/* eslint-enable */
